import { useState, useEffect, useMemo } from "react";
import { Box, Input, Text, Select, createListCollection } from "@chakra-ui/react";
import { Player } from "@/types";
import { players_search_index_path } from "@/routes";

interface PlayerSearchInputProps {
  selectedPlayers: Player[];
  onPlayerChange: (player: Player) => void;
  placeholder: string;
  minSearchLength?: number;
}

const PlayerSearchInput = ({ selectedPlayers, onPlayerChange, placeholder, minSearchLength = 1 }: PlayerSearchInputProps) => {
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<Player[]>([]);

  // 検索結果のコレクション
  const searchCollection = useMemo(() => {
    return createListCollection({
      items: searchResults,
      itemToString: player => player.display_name,
      itemToValue: player => player.player_id,
    });
  }, [searchResults]);

  // プレイヤー検索
  const searchPlayers = async (query: string) => {
    if (query.trim().length < minSearchLength) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await fetch(players_search_index_path({ q: query }));
      if (response.ok) {
        const results = await response.json();
        const players = results.players || [];
        setSearchResults(players.filter((p: Player) => !selectedPlayers.some(sp => sp.player_id === p.player_id)));
      }
    } catch (error) {
      console.error("プレイヤー検索エラー:", error);
    }
  };

  useEffect(() => {
    searchPlayers(search);
  }, [search, selectedPlayers]);

  const addPlayer = (player: Player) => {
    onPlayerChange(player);
    setSearch("");
    setSearchResults([]);
  };

  const handlePlayerSelect = (details: any) => {
    const selectedValue = details.value[0];
    const player = searchResults.find(p => p.player_id === selectedValue);
    if (player) {
      addPlayer(player);
    }
  };

  return (
    <Box position="relative">
      <Input
        value={search}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
        placeholder={placeholder}
        borderRadius="lg"
        bg="gray.50"
        _focus={{ bg: "white", borderColor: "blue.400", shadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
        autoComplete="off"
      />

      {/* 検索結果をSelectで表示 */}
      <Select.Root
        collection={searchCollection}
        size="sm"
        onValueChange={handlePlayerSelect}
        value={[]}
        open={true}
        style={{
          visibility: searchResults.length > 0 ? "visible" : "hidden",
        }}
        positioning={{
          placement: "bottom-start",
          strategy: "absolute",
        }}
      >
        <Select.Trigger display="none">
          <Select.ValueText placeholder="検索結果から選択" />
        </Select.Trigger>
        <Select.Content
          position="absolute"
          left="0"
          right="0"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          maxH="40"
          overflowY="auto"
          zIndex={10}
          bg="white"
          shadow="md"
        >
          {searchCollection.items.map(player => (
            <Select.Item item={player} key={player.player_id}>
              <Text fontWeight="semibold">{player.display_name}</Text>
              <Text fontSize="xs" color="gray.500">
                {player.player_id}
              </Text>
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
    </Box>
  );
};

export default PlayerSearchInput;
